import { Component, Inject, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { NgSelectComponent } from '@ng-select/ng-select';

export interface FormFieldConfig {
    key: string;
    label: string;
    type: 'text' | 'textarea' | 'number' | 'date' | 'datetime-local' | 'select' | 'ngselect' | 'email-chips' | 'autocomplete';
    required?: boolean;
    maxLength?: number;
    options?: { value: any; label: string; priority?: boolean }[]; // For select fields
    rows?: number; // For textarea
    placeholder?: string;
    validators?: any[];
    width?: string; // Custom width (e.g., '50%', '100%')
    colSpan?: number; // Number of columns to span in the layout
    legend?: string; // Optional legend text to display below the field
    disabled?: boolean; // Whether the field is disabled
    dependsOn?: string; // Key of the field this depends on
    showWhen?: (value: any) => boolean; // Function to determine if field should be shown
    min?: string; // Minimum value for date/number inputs
    max?: string; // Maximum value for date/number inputs
    defaultValue?: any; // Default value for the field
    requiredMessage?: string; // Custom message when required validation fails
    suggestions?: { label: string; value: string }[]; // Selectable suggestions for email-chips fields
    restrictToSuggestions?: boolean; // Only allow email-chips values that exist in suggestions
}

export interface AddEditDialogConfig {
    title: string;
    fields: FormFieldConfig[];
    apiEndpoint: string;
    data?: any; // Existing data for edit mode
    isEditMode?: boolean;
    submitButtonText?: string;
    successMessage?: string;
    mapDataToApi?: (formData: any, additionalData: any) => any; // Function to transform form data before sending
    additionalData?: any; // Additional data to pass to mapDataToApi
    onFieldChange?: (fieldKey: string, value: any, fields: FormFieldConfig[], setTitle: (title: string) => void) => void; // Callback when a field value changes
    showDeleteButton?: boolean; // Show delete button
    deleteButtonText?: string; // Text for delete button
    onDelete?: () => Promise<boolean>; // Delete callback function
    headerAction?: { // Optional action button in header
        icon: string; // Material icon name
        tooltip: string; // Tooltip text
        color?: string; // Icon color
        onClick: () => void; // Click handler
    };
}

@Component({
    selector: 'app-add-edit-dialog',
    templateUrl: './add-edit-dialog.component.html',
    styleUrls: ['./add-edit-dialog.component.less'],
    standalone: false
})
export class AddEditDialogComponent implements OnInit {
    form!: FormGroup;
    isEditMode: boolean = false;
    isSubmitting: boolean = false;
    isDeleting: boolean = false;
    fields: FormFieldConfig[] = [];
    title: string = '';
    submitButtonText: string = '';
    showDeleteButton: boolean = false;
    deleteButtonText: string = '';
    headerAction?: { icon: string; tooltip: string; color?: string; onClick: () => void };

    constructor(
        public dialogRef: MatDialogRef<AddEditDialogComponent>,
        private readonly formBuilder: FormBuilder,
        private readonly http: HttpClient,
        @Inject('BASE_URL') private readonly baseUrl: string,
        @Inject(MAT_DIALOG_DATA) public config: AddEditDialogConfig,
        private readonly toastrService: ToastrService
    ) {
        this.isEditMode = config.isEditMode || false;
        this.fields = config.fields;
        this.title = config.title;
        this.submitButtonText = config.submitButtonText || (this.isEditMode ? 'Update' : 'Create');
        this.showDeleteButton = config.showDeleteButton || false;
        this.deleteButtonText = config.deleteButtonText || 'Delete';
        this.headerAction = config.headerAction;
    }

    ngOnInit(): void {
        this.initializeForm();
        this.setupFieldChangeListeners();
        this.setupConditionalFieldsListeners();
    }

    private setupConditionalFieldsListeners(): void {
        // Find all fields that have dependencies
        const dependentFields = this.fields.filter(f => f.dependsOn);
        
        dependentFields.forEach(field => {
            if (field.dependsOn) {
                const parentControl = this.form.get(field.dependsOn);
                if (parentControl) {
                    // Listen for changes in parent field
                    parentControl.valueChanges.subscribe(value => {
                        this.updateFieldVisibility(field, value);
                    });
                    // Set initial visibility
                    this.updateFieldVisibility(field, parentControl.value);
                }
            }
        });
    }

    private updateFieldVisibility(field: FormFieldConfig, parentValue: any): void {
        const control = this.form.get(field.key);
        if (!control) return;

        const shouldShow = field.showWhen ? field.showWhen(parentValue) : true;
        
        if (shouldShow) {
            // Show field and restore validators
            const validators: any[] = [];
            if (field.required) {
                validators.push(Validators.required);
            }
            if (field.maxLength) {
                validators.push(Validators.maxLength(field.maxLength));
            }
            if (field.validators) {
                validators.push(...field.validators);
            }
            control.setValidators(validators);
        } else {
            // Hide field and clear its value
            control.setValue(field.type === 'email-chips' ? [] : '');
            control.clearValidators();
        }
        control.updateValueAndValidity();
    }

    isFieldVisible(field: FormFieldConfig): boolean {
        if (!field.dependsOn || !field.showWhen) {
            return true;
        }
        
        const parentControl = this.form.get(field.dependsOn);
        if (!parentControl) {
            return true;
        }
        
        return field.showWhen(parentControl.value);
    }

    private setupFieldChangeListeners(): void {
        if (this.config.onFieldChange) {
            this.form.valueChanges.subscribe(() => {
                // Check each field for changes
                Object.keys(this.form.controls).forEach(key => {
                    const control = this.form.get(key);
                    if (control) {
                        this.config.onFieldChange!(key, control.value, this.fields, (newTitle: string) => {
                            this.title = newTitle;
                        });
                    }
                });
            });
        }
    }

    private initializeForm(): void {
        const formGroup: any = {};
        const existingData = this.config.data || {};

        this.fields.forEach(field => {
            const validators = this.buildBaseValidators(field);
            let defaultValue = this.resolveDefaultValue(field, existingData);

            if (field.type === 'autocomplete') {
                this.setupAutocompleteField(field, defaultValue, validators);
            }
            if (field.type === 'email-chips') {
                defaultValue = this.setupEmailChipsField(field, defaultValue, validators);
            }

            formGroup[field.key] = [{ value: defaultValue, disabled: !!field.disabled }, validators];
        });

        this.form = this.formBuilder.group(formGroup);
        this.form.markAsPristine();
    }

    private buildBaseValidators(field: FormFieldConfig): any[] {
        const validators: any[] = [];

        if (field.required) {
            validators.push(Validators.required);
        }

        // email-chips hold arrays; their length is enforced by the joined-string validator instead
        if (field.maxLength && field.type !== 'email-chips') {
            validators.push(Validators.maxLength(field.maxLength));
        }

        if (field.validators) {
            validators.push(...field.validators);
        }

        return validators;
    }

    private resolveDefaultValue(field: FormFieldConfig, existingData: any): any {
        let defaultValue = existingData[field.key];
        // Use defaultValue from field config if no existing data and not in edit mode
        if (defaultValue === undefined && !this.isEditMode && field.defaultValue !== undefined) {
            defaultValue = field.defaultValue;
        }
        defaultValue ??= '';
        if ((field.type === 'date' || field.type === 'datetime-local') && defaultValue) {
            defaultValue = this.formatDateForInput(defaultValue, field.type);
        }
        return defaultValue;
    }

    private setupAutocompleteField(field: FormFieldConfig, defaultValue: any, validators: any[]): void {
        const current = (defaultValue || '').toString().trim();
        if (current.length > 0) {
            field.suggestions = this.mergeMissingSuggestions(field.suggestions, [current]);
        }
        if (field.restrictToSuggestions) {
            validators.push(this.valueInSuggestionsValidator(field));
        }
    }

    private setupEmailChipsField(field: FormFieldConfig, defaultValue: any, validators: any[]): string[] {
        const values = this.parseEmailList(defaultValue);
        field.suggestions = this.mergeMissingSuggestions(field.suggestions, values);
        if (field.maxLength) {
            validators.push(this.joinedMaxLengthValidator(field.maxLength));
        }
        if (field.restrictToSuggestions) {
            validators.push(this.valuesInSuggestionsValidator(field));
        }
        return values;
    }

    private parseEmailList(value: any): string[] {
        if (typeof value === 'string') {
            return value.split(';').map(part => part.trim()).filter(part => part.length > 0);
        }
        return Array.isArray(value) ? value : [];
    }

    private valueInSuggestionsValidator(field: FormFieldConfig) {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = (control.value || '').toString().trim();
            if (value.length === 0) {
                return null;
            }
            const inList = (field.suggestions || []).some(s => s.value.toLowerCase() === value.toLowerCase());
            return inList ? null : { notInList: true };
        };
    }

    private valuesInSuggestionsValidator(field: FormFieldConfig) {
        return (control: AbstractControl): ValidationErrors | null => {
            const selected: string[] = Array.isArray(control.value) ? control.value : [];
            const allowed = new Set((field.suggestions || []).map(s => s.value.toLowerCase()));
            const notInList = selected.filter(v => !allowed.has((v || '').toLowerCase()));
            return notInList.length > 0 ? { emailsNotInList: notInList } : null;
        };
    }

    private joinedMaxLengthValidator(maxLength: number) {
        return (control: AbstractControl): ValidationErrors | null => {
            const selected: string[] = Array.isArray(control.value) ? control.value : [];
            return selected.join(';').length > maxLength ? { maxlength: { requiredLength: maxLength } } : null;
        };
    }

    private formatDateForInput(date: any, type: string): string {
        if (!date) return '';
        
        const dateObj = new Date(date);
        if (Number.isNaN(dateObj.getTime())) return '';

        if (type === 'date') {
            return dateObj.toISOString().split('T')[0];
        } else if (type === 'datetime-local') {
            return dateObj.toISOString().slice(0, 16);
        }
        
        return '';
    }

    onSubmit(): void {
        if (this.form.invalid || this.isSubmitting) {
            return;
        }

        this.isSubmitting = true;

        // Transform form data
        let formData: any = {};
        this.fields.forEach(field => {
            let value = this.form.getRawValue()[field.key];

            if (field.type === 'email-chips' && Array.isArray(value)) {
                value = value.join(';');
            }

            // Trim strings
            if (typeof value === 'string') {
                value = value.trim() || null;
            }
            
            formData[field.key] = value;
        });

        // Apply custom mapping if provided
        if (this.config.mapDataToApi) {
            formData = this.config.mapDataToApi(formData, this.config.additionalData);
        }

        const url = `${this.baseUrl}${this.config.apiEndpoint}`;
        const request = this.isEditMode 
            ? this.http.put(url, formData) 
            : this.http.post(url, formData);

        request.subscribe({
            next: (result: any) => {
                const message = this.config.successMessage 
                    || `Item was ${this.isEditMode ? 'updated' : 'created'} successfully!`;
                this.toastrService.success(message);
                this.dialogRef.close(true);
            },
            error: (error) => {
                console.error('Error saving item:', error);
                this.isSubmitting = false;
                
                let errorMessage = 'An unexpected error occurred. Please contact administrator.';
                
                if ((error.status === 400 || error.status === 409) && error.error?.message) {
                    errorMessage = error.error.message;
                }
                
                this.toastrService.error(errorMessage);
            }
        });
    }

    backButtonHandler(event?: Event): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.dialogRef.close(false);
    }

    async onDelete(event?: Event): Promise<void> {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (!this.config.onDelete || this.isDeleting) {
            return;
        }

        this.isDeleting = true;

        try {
            const success = await this.config.onDelete();
            if (success) {
                this.dialogRef.close(true);
            }
        } catch (error) {
            console.error('Error in delete handler:', error);
        } finally {
            this.isDeleting = false;
        }
    }

    get canSubmit(): boolean {
        if (this.isEditMode) {
            return this.form.valid && this.form.dirty && !this.isSubmitting;
        }
        return this.form.valid && !this.isSubmitting;
    }

    getFieldError(fieldKey: string): string {
        const field = this.fields.find(f => f.key === fieldKey);
        const control = this.form.get(fieldKey);
        
        if (!control || !field) return '';
        
        if (control.hasError('required')) {
            return field.requiredMessage || `${field.label} is required`;
        }
        
        if (control.hasError('maxlength')) {
            return `${field.label} cannot exceed ${field.maxLength} characters`;
        }

        if (control.hasError('email')) {
            return `${field.label} must be a valid email address`;
        }

        if (control.hasError('notInList')) {
            return `${field.label} must be selected from the user list`;
        }

        if (control.hasError('emailsNotInList')) {
            const notInList = control.getError('emailsNotInList');
            return Array.isArray(notInList) && notInList.length > 0
                ? `Not in the user list: ${notInList.join(', ')}`
                : 'Only users from the list can be added';
        }

        return 'Invalid value';
    }

    hasError(fieldKey: string): boolean {
        const control = this.form.get(fieldKey);
        return !!(control && control.invalid && (control.dirty || control.touched));
    }

    clearSelectSearch(select: NgSelectComponent): void {
        select.filter('');
    }

    searchUserSuggestion(term: string, item: { label: string; value: string }): boolean {
        const search = term.toLowerCase();
        return (item.label || '').toLowerCase().includes(search)
            || (item.value || '').toLowerCase().includes(search);
    }

    private mergeMissingSuggestions(
        suggestions: { label: string; value: string }[] | undefined,
        values: string[]
    ): { label: string; value: string }[] {
        const list = suggestions || [];
        const existing = new Set(list.map(s => s.value.toLowerCase()));
        const missing = values
            .filter(v => !!v && !existing.has(v.toLowerCase()))
            .map(v => ({ label: v, value: v }));
        return missing.length > 0 ? [...missing, ...list] : list;
    }

    compareValues(item: any, selected: any): boolean {
        // Extract value from item if it's an object with a value property
        const itemValue = (item && typeof item === 'object' && 'value' in item) ? item.value : item;
        const selectedValue = (selected && typeof selected === 'object' && 'value' in selected) ? selected.value : selected;
        
        // Compare the extracted values
        return itemValue === selectedValue;
    }
}
